import type { CommentHubDevice, CommentHubDevicesResponse } from 'shared-code/commenthub-api'

/**
 * The reader's phones as the AA app holds them ("Your device", docs/your-device-plan.md).
 * `ready` is the only status that says anything about the reader: `loading`/`error`/`unavailable`
 * (no commentHub site configured, or signed out → `idle`) never mean "no phone" — nothing that
 * asks for a phone (the step, the menu row, the pill popover) may fire on them.
 */
export type MyDevicesStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable'

export interface MyDevicesData {
  status: MyDevicesStatus
  current: CommentHubDevice | null
  previous: CommentHubDevice[]
}

export const EMPTY_DEVICES: MyDevicesData = { status: 'idle', current: null, previous: [] }

/** The reader is known to have no current phone — the one state that may ask for one. */
export const knownWithoutPhone = (d: Pick<MyDevicesData, 'status' | 'current'>): boolean =>
  d.status === 'ready' && d.current === null

export type DevicesAction =
  | { type: 'status'; status: MyDevicesStatus }
  | { type: 'loaded'; list: CommentHubDevicesResponse }
  /** A row's fields changed (optimistic or the server's answer). */
  | { type: 'patch'; id: number; patch: Partial<CommentHubDevice> }
  /** Remove: the current phone joins history at `endedAt`, switched off. */
  | { type: 'removeCurrent'; id: number; endedAt: string }
  /** Forget: the row leaves the history. */
  | { type: 'forget'; id: number }

const byNewest = (a: CommentHubDevice, b: CommentHubDevice): number =>
  (b.endedAt ?? '').localeCompare(a.endedAt ?? '') || b.startedAt.localeCompare(a.startedAt)

function patchRow(
  d: CommentHubDevice,
  id: number,
  patch: Partial<CommentHubDevice>
): CommentHubDevice {
  if (d.id !== id) {
    return d
  }
  return { ...d, ...patch }
}

export function devicesReducer(state: MyDevicesData, action: DevicesAction): MyDevicesData {
  switch (action.type) {
    case 'status':
      return action.status === 'ready'
        ? { ...state, status: 'ready' }
        : { ...EMPTY_DEVICES, status: action.status }
    case 'loaded':
      return {
        status: 'ready',
        current: action.list.current,
        previous: [...action.list.previous].sort(byNewest),
      }
    case 'patch': {
      const apply = (d: CommentHubDevice): CommentHubDevice => patchRow(d, action.id, action.patch)
      return {
        ...state,
        current: state.current ? apply(state.current) : null,
        previous: state.previous.map(apply).sort(byNewest),
      }
    }
    case 'removeCurrent': {
      if (!state.current || state.current.id !== action.id) {
        return state
      }
      const moved = { ...state.current, endedAt: action.endedAt, showOnComments: false }
      return { ...state, current: null, previous: [moved, ...state.previous].sort(byNewest) }
    }
    case 'forget':
      return { ...state, previous: state.previous.filter(d => d.id !== action.id) }
    default:
      return state
  }
}
